import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';
import SearchBox, { loadMedicineOptions } from './SearchBox';

function rupee(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${date.getFullYear()}`;
}

async function authorizedGet(path) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Sign in again to load this list.');
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Could not load this list.');
  return data;
}

function DataTable({ headers, rows, empty }) {
  if (!rows.length) return <p style={{ color: '#6c757d' }}>{empty}</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) => (
                <td key={index} style={{ padding: '8px', borderBottom: '1px solid #f1f3f5', whiteSpace: row.nowrap && row.nowrap[index] ? 'nowrap' : undefined }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LowStock({ medicines }) {
  const [windowDays, setWindowDays] = useState(30);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limit = new Date(today);
  limit.setDate(limit.getDate() + windowDays);
  const low = medicines.filter((medicine) => {
    const stock = Number(medicine.QUANTITY_IN_STOCK) || 0;
    const reorder = Number(medicine.REORDER_LEVEL) || 0;
    return stock === 0 || medicine.STATUS === 'Out of Stock' || (reorder > 0 && stock <= reorder);
  });
  const expiry = medicines.filter((medicine) => {
    if (!medicine.EXPIRATION_DATE) return false;
    const date = new Date(medicine.EXPIRATION_DATE);
    return !Number.isNaN(date.getTime()) && date < limit;
  }).sort((left, right) => new Date(left.EXPIRATION_DATE) - new Date(right.EXPIRATION_DATE));
  return (
    <div>
      <h4 style={{ marginTop: 0 }}>Low stock</h4>
      <DataTable
        headers={['Medicine', 'Stock', 'Reorder at', 'Status']}
        empty="No medicine is at or below its reorder level."
        rows={low.map((medicine) => ({
          key: medicine._id,
          cells: [
            `${medicine.MEDICINE_NAME}${medicine.STRENGTH ? ` · ${medicine.STRENGTH}` : ''}`,
            medicine.QUANTITY_IN_STOCK ?? 0,
            medicine.REORDER_LEVEL || '—',
            medicine.STATUS || '—'
          ]
        }))}
      />
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '1.25rem 0 0.6rem' }}>
        <h4 style={{ margin: 0 }}>Expiry</h4>
        {[30, 60, 90].map((days) => (
          <button key={days} type="button" onClick={() => setWindowDays(days)} style={choiceStyle(windowDays === days)}>{days} days</button>
        ))}
      </div>
      <DataTable
        headers={['Medicine', 'Batch', 'Expires', 'Stock']}
        empty={`Nothing expires within ${windowDays} days.`}
        rows={expiry.map((medicine) => ({
          key: `${medicine._id}-expiry`,
          cells: [
            `${medicine.MEDICINE_NAME}${medicine.STRENGTH ? ` · ${medicine.STRENGTH}` : ''}`,
            medicine.BATCH_NUMBER || '—',
            formatDate(medicine.EXPIRATION_DATE),
            medicine.QUANTITY_IN_STOCK ?? 0
          ]
        }))}
      />
    </div>
  );
}

function PurchaseHistory() {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const search = query.trim() ? `&searchTerm=${encodeURIComponent(query.trim())}` : '';
        const data = await authorizedGet(`/medicinePurchases?limit=100${search}`);
        if (!cancelled) setRows(Array.isArray(data.purchases) ? data.purchases : []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(load, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);
  return (
    <div>
      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search medicine, supplier, batch, or receipt" style={inputStyle} />
      {loading && <p>Loading purchases...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && (
        <DataTable
          headers={['Date', 'Receipt', 'Medicine', 'Qty', 'Price', 'Total', 'Supplier', 'Batch', 'Expires']}
          empty="No purchase receipts match this search."
          rows={rows.map((purchase) => ({
            key: purchase._id || purchase.PURCHASE_ID,
            nowrap: [true, true],
            cells: [
              formatDate(purchase.PURCHASE_DATE),
              purchase.PURCHASE_ID,
              purchase.MEDICINE_NAME,
              purchase.QUANTITY_PURCHASED,
              rupee(purchase.UNIT_PURCHASE_PRICE),
              rupee(purchase.TOTAL_PURCHASE_AMOUNT),
              purchase.SUPPLIER_NAME || '—',
              purchase.BATCH_NUMBER || '—',
              purchase.EXPIRATION_DATE ? formatDate(purchase.EXPIRATION_DATE) : '—'
            ]
          }))}
        />
      )}
    </div>
  );
}

function Suppliers() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    authorizedGet('/medicinePurchases?limit=200')
      .then((data) => {
        if (cancelled) return;
        const map = new Map();
        (data.purchases || []).forEach((purchase) => {
          const name = purchase.SUPPLIER_NAME || 'No supplier recorded';
          const group = map.get(name) || { name, contact: purchase.SUPPLIER_CONTACT || '', count: 0, total: 0, last: purchase.PURCHASE_DATE, medicines: new Map() };
          group.count += 1;
          group.total += Number(purchase.TOTAL_PURCHASE_AMOUNT) || 0;
          if (!group.contact && purchase.SUPPLIER_CONTACT) group.contact = purchase.SUPPLIER_CONTACT;
          if (new Date(purchase.PURCHASE_DATE) > new Date(group.last)) group.last = purchase.PURCHASE_DATE;
          const medicine = group.medicines.get(purchase.MEDICINE_NAME) || { name: purchase.MEDICINE_NAME, price: purchase.UNIT_PURCHASE_PRICE, date: purchase.PURCHASE_DATE };
          if (new Date(purchase.PURCHASE_DATE) >= new Date(medicine.date)) {
            medicine.price = purchase.UNIT_PURCHASE_PRICE;
            medicine.date = purchase.PURCHASE_DATE;
          }
          group.medicines.set(purchase.MEDICINE_NAME, medicine);
          map.set(name, group);
        });
        setGroups([...map.values()].sort((left, right) => right.total - left.total));
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading) return <p>Loading suppliers...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!groups.length) return <p style={{ color: '#6c757d' }}>No purchase receipts yet.</p>;
  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      {groups.map((group) => (
        <div key={group.name} style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '0.85rem' }}>
          <strong>{group.name}</strong>
          <div style={{ color: '#495057', fontSize: '0.9rem', marginTop: '0.25rem' }}>
            {group.contact || 'No contact'} · {group.count} {group.count === 1 ? 'receipt' : 'receipts'} · {rupee(group.total)} · Last {formatDate(group.last)}
          </div>
          <div style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.35rem' }}>
            {[...group.medicines.values()].map((medicine) => `${medicine.name} ${rupee(medicine.price)}`).join(' · ')}
          </div>
        </div>
      ))}
    </div>
  );
}

function CounterSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    authorizedGet('/medicineAdjustments/counter')
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading) return <p>Loading today's counter...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  const cards = [
    ['Sales', summary.salesCount],
    ['Collected', rupee(summary.collected)],
    ['Returns', summary.returnCount],
    ['Refunds', rupee(summary.refunds)],
    ['Net collected', rupee(summary.netCollected)],
    ['Estimated profit', rupee(summary.estimatedProfit)]
  ];
  return (
    <div>
      <p style={{ marginTop: 0, color: '#6c757d' }}>Today, {formatDate(summary.date)}. Profit uses each sale price minus the medicine's current purchase price, then subtracts refunds. Stock removed today: {summary.stockRemoved}.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
        {cards.map(([label, value]) => (
          <div key={label} style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '0.85rem' }}>
            <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>{label}</div>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function Adjustments() {
  const [query, setQuery] = useState('');
  const [medicine, setMedicine] = useState(null);
  const [type, setType] = useState('Return');
  const [quantity, setQuantity] = useState('1');
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadRows = () => authorizedGet('/medicineAdjustments?limit=30').then((data) => setRows(data.adjustments || []));
  useEffect(() => { loadRows().catch((err) => setError(err.message)); }, []);

  const save = async () => {
    if (!medicine) {
      setError('Select a medicine.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/medicineAdjustments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          MEDICINE_ID: medicine._id || medicine.MEDICINE_ID,
          ADJUSTMENT_TYPE: type,
          QUANTITY: quantity,
          COUNTED_QUANTITY: counted,
          REASON: reason
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not update the stock.');
      setMessage(`${medicine.MEDICINE_NAME} now has ${data.adjustment.STOCK_AFTER} in stock.`);
      setReason('');
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 640, marginBottom: '1rem' }}>
        <SearchBox
          placeholder="Search medicine"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setMedicine(null);
          }}
          loadOptions={loadMedicineOptions}
          onSelect={(option) => {
            setMedicine(option.item);
            setQuery(option.label);
            setCounted(String(option.item.QUANTITY_IN_STOCK ?? ''));
          }}
        />
        <select value={type} onChange={(event) => setType(event.target.value)} style={inputStyle}>
          <option>Return</option>
          <option>Damage</option>
          <option>Expired</option>
          <option>Count correction</option>
        </select>
        {type === 'Count correction' ? (
          <input type="number" min="0" value={counted} onChange={(event) => setCounted(event.target.value)} placeholder="Quantity counted on the shelf" style={inputStyle} />
        ) : (
          <input type="number" min="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder="Quantity" style={inputStyle} />
        )}
        <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Note" style={inputStyle} />
        {medicine && <p style={{ margin: 0, color: '#495057' }}>On hand: {medicine.QUANTITY_IN_STOCK ?? 0} · Selling price {rupee(medicine.SELLING_PRICE)}{type === 'Return' ? ` · Refund ${rupee((Number(medicine.SELLING_PRICE) || 0) * (Number(quantity) || 0))}` : ''}</p>}
        <button type="button" onClick={save} disabled={saving} style={{ padding: '10px', backgroundColor: '#1f4d40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
          {saving ? 'Saving...' : 'Update stock'}
        </button>
        {message && <p style={{ color: '#198754', margin: 0 }}>{message}</p>}
        {error && <p style={{ color: 'red', margin: 0 }}>{error}</p>}
      </div>
      <DataTable
        headers={['Date', 'Medicine', 'Reason', 'Change', 'Stock after', 'Refund']}
        empty="No returns or adjustments yet."
        rows={rows.map((row) => ({
          key: row._id,
          cells: [
            formatDate(row.ADJUSTMENT_DATE),
            row.MEDICINE_NAME,
            row.ADJUSTMENT_TYPE,
            row.QUANTITY_CHANGE > 0 ? `+${row.QUANTITY_CHANGE}` : row.QUANTITY_CHANGE,
            row.STOCK_AFTER,
            row.REFUND_AMOUNT ? rupee(row.REFUND_AMOUNT) : '—'
          ]
        }))}
      />
    </div>
  );
}

function PharmacyOperations({ section }) {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(section === 'lowStock');
  const [error, setError] = useState('');
  useEffect(() => {
    if (section !== 'lowStock') return undefined;
    let cancelled = false;
    authorizedGet('/medicines')
      .then((data) => { if (!cancelled) setMedicines(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [section]);

  const titles = {
    lowStock: 'Low stock and expiry',
    purchaseHistory: 'Purchase history',
    adjustments: 'Returns and adjustments',
    suppliers: 'Suppliers',
    counter: 'Daily counter summary'
  };
  return (
    <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
      <h3 style={{ marginTop: 0 }}>{titles[section]}</h3>
      {section === 'lowStock' && loading && <p>Loading medicines...</p>}
      {section === 'lowStock' && error && <p style={{ color: 'red' }}>{error}</p>}
      {section === 'lowStock' && !loading && !error && <LowStock medicines={medicines} />}
      {section === 'purchaseHistory' && <PurchaseHistory />}
      {section === 'adjustments' && <Adjustments />}
      {section === 'suppliers' && <Suppliers />}
      {section === 'counter' && <CounterSummary />}
    </div>
  );
}

function choiceStyle(active) {
  return {
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid #007bff',
    backgroundColor: active ? '#007bff' : 'white',
    color: active ? 'white' : '#007bff',
    cursor: 'pointer'
  };
}

const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };

export default PharmacyOperations;
